/**
 * Aba Alertas — promoção ativa rendendo pouco (com SAIR), CMV ausente, CMV velho, sem venda em
 * 30 dias e erros de API na coleta.
 */
import { Box, Stack, Typography } from '@mui/material'
import { CORTE_MC_ALERTA, ativasComMcBaixa, classeMc } from './logic'
import { RevisaoHumana, TetoDesconto } from './RevisaoHumana'
import { usePromo } from './ctx'
import { BotaoSair, IncMark, LARANJA, McTip, P, Panel, brl, mono, pct, useCorMc } from './ui'

function Linha({ children }: { children: React.ReactNode }) {
  return <Box sx={{ display: 'grid', gridTemplateColumns: { xs: '1fr', md: '1fr auto auto' }, gap: 1.75, alignItems: 'center', py: 1, borderTop: 1, borderColor: 'divider', fontSize: 13 }}>{children}</Box>
}

export function AbaAlertas() {
  const ctx = usePromo()
  const { conta, base, cmvManual, alvo } = ctx
  const cor = useCorMc()
  const { abaixo, semConta } = ativasComMcBaixa(conta, base, cmvManual)

  return (
    <Stack spacing={2}>
      <RevisaoHumana />
      <TetoDesconto />
      <Panel tone="critico" title={`🔻 Promoção ativa com M.C. abaixo de ${CORTE_MC_ALERTA}% — ${abaixo.length} ${abaixo.length === 1 ? 'anúncio' : 'anúncios'}`}>
        <P>Anúncios <b>participando agora</b> de uma promoção cuja margem, no preço promocional, fica abaixo de {CORTE_MC_ALERTA}%. A M.C. sai dos dados do próprio anúncio na API — comissão e frete no preço da promoção, custo do cadastro e alíquota de imposto — nunca de média de margem. O botão <b>SAIR</b> tira o anúncio da promoção{ctx.escreve ? ' — e aqui ele escreve de verdade na API, pedindo confirmação em dois cliques.' : '; como a tela está em preview, ele não envia nada ao Mercado Livre.'}
          {semConta.length ? <><br />⚠ Outras <b>{semConta.length}</b> promoções ativas ficaram fora desta lista porque a margem delas não fecha (falta custo, comissão ou frete) — não dá para dizer se estão acima ou abaixo do corte.</> : null}</P>
        {abaixo.length ? abaixo.map(({ a, p, m }) => (
          <Linha key={`${a.mlb}-${p.id}`}>
            <Box sx={{ minWidth: 0 }}><Typography variant="body2" noWrap title={a.titulo}>{a.titulo}</Typography><Typography sx={{ fontSize: 11, color: 'text.disabled' }}><Box component="span" sx={mono}>{a.mlb}</Box> · {p.name || p.type_label} · de {brl(p.original_price ?? a.preco_atual)} por <b>{brl(p.preco_promo)}</b></Typography></Box>
            <McTip a={a} p={p}><Box component="b" sx={{ color: cor(classeMc(m, alvo)) }}>{brl(m.rs)} · {pct(m.pct)}</Box><IncMark faltas={m.faltas} />{m.faltas.length ? <Typography sx={{ fontSize: 10.5, color: LARANJA }}>falta {m.faltas.join(', ')} — número otimista</Typography> : null}</McTip>
            <BotaoSair a={a} p={p} />
          </Linha>
        )) : <Typography sx={{ color: 'text.disabled', fontSize: 13 }}>Nenhuma promoção ativa abaixo de {CORTE_MC_ALERTA}%.</Typography>}
      </Panel>

      <Panel tone="critico" title={`⚠ CMV ausente — ${conta.alertas_cmv_ausente.length} anúncios`}>
        <P>Sem CMV cadastrado (ou sem SKU no item), essas promoções ficam <b>fora da simulação de margem</b> — regra da skill fiesta-base-dados: CMV zero nunca entra no cálculo. Informe o custo na aba "Por anúncio" para ver a margem (correção de leitura; não corrige a base).</P>
        {conta.alertas_cmv_ausente.length ? conta.alertas_cmv_ausente.map((x) => (
          <Linha key={x.mlb}>
            <Box sx={{ minWidth: 0 }}><Typography variant="body2" noWrap title={x.titulo}>{x.titulo}</Typography><Typography sx={{ fontSize: 11, color: 'text.disabled', ...mono }}>{x.mlb}</Typography></Box>
            <Box sx={{ color: 'text.disabled', fontSize: 12 }}>{x.sku || 'sem SKU'}</Box>
            <Box sx={{ color: 'warning.main', fontSize: 11 }}>{x.motivo}</Box>
          </Linha>
        )) : <Typography sx={{ color: 'text.disabled', fontSize: 13 }}>Nenhum.</Typography>}
      </Panel>

      <Panel tone="alert" title={`⚠ CMV desatualizado (>180 dias) — ${conta.alertas_cmv_velho.length} anúncios`}>
        <P>O CMV entra no cálculo, mas pode estar defasado — custo velho distorce a margem simulada tanto quanto custo ausente.</P>
        {conta.alertas_cmv_velho.length ? conta.alertas_cmv_velho.map((x) => (
          <Linha key={x.mlb}>
            <Box sx={{ minWidth: 0 }}><Typography variant="body2" noWrap title={x.titulo}>{x.titulo}</Typography><Typography sx={{ fontSize: 11, color: 'text.disabled', ...mono }}>{x.mlb}</Typography></Box>
            <Box sx={{ color: 'text.disabled', fontSize: 12 }}>{x.sku} · {brl(x.cmv)}</Box>
            <Box sx={{ color: 'warning.main', fontSize: 11 }}>{x.dias} dias sem atualizar ({x.data})</Box>
          </Linha>
        )) : <Typography sx={{ color: 'text.disabled', fontSize: 13 }}>Nenhum.</Typography>}
      </Panel>

      <Panel title={`Sem venda nos últimos 30 dias — ${conta.sem_venda_recente} anúncios`}>
        <P>Sem pedido recente em margens, não há alíquota de imposto própria do anúncio — a tela herda a média ponderada da categoria, sempre rotulada como estimativa na memória de cálculo.</P>
      </Panel>

      <Panel title="Erros de API na coleta">
        {(conta.erros_api || []).length ? conta.erros_api.map((e, i) => (
          <Linha key={`${e.item_id}-${i}`}><Box sx={mono}>{e.item_id}</Box><Box sx={{ color: 'text.disabled', fontSize: 12 }}>{e.endpoint}</Box><Box sx={{ color: 'warning.main', fontSize: 11 }}>HTTP {e.status}</Box></Linha>
        )) : <Typography sx={{ color: 'text.disabled', fontSize: 13 }}>Nenhum erro de API na coleta.</Typography>}
      </Panel>
    </Stack>
  )
}
